import React, { useEffect, useState } from 'react';
import { DndContext, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { SortableItem } from './SortableItem';
import { TrashIcon, PhotoIcon } from '@heroicons/react/24/solid';
import ImageSelectModal from './ImageSelectModal';
import ProductImageViewer from './ProductImageViewer';
import AddImagePlaceholder from './AddImagePlaceholder';
import { useProductImages } from './ProductImagesProvider';
import { Badge } from '../primitives';
import './ProductImage.css';

interface ProductImageManagerProps {
    disabled?: boolean;
}

const ProductImageManager: React.FC<ProductImageManagerProps> = ({ disabled = false }) => {
    const [showModal, setShowModal] = useState(false);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(0);

    const { productId, selectedImageIds, selectedImages, removeImage, selectImages, fetchImages } = useProductImages();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        })
    );

    useEffect(() => {
        if (showModal) {
            fetchImages();
        }
    }, [showModal]);

    const handleDragEnd = (event: any) => {
        if (disabled) return; // Prevent reordering if disabled

        const { active, over } = event;
        // `over` is null when the item is dropped outside any droppable —
        // dereferencing it crashed the editor.
        if (over && active.id !== over.id) {
            const oldIndex = selectedImages.findIndex((item) => item.id === active.id);
            const newIndex = selectedImages.findIndex((item) => item.id === over.id);

            // Use selectImages to update the provider with the new order
            const reorderedIds = arrayMove(selectedImages.map(image => image.id), oldIndex, newIndex);
            selectImages(reorderedIds);
        }
    };

    const handleDeleteImage = (index: number, e: React.MouseEvent) => {
        e.stopPropagation(); // Stop the event from propagating to the parent div
        if (!disabled) {
            const imageIdToDelete = selectedImages[index].id;
            removeImage(imageIdToDelete);
        }
    };

    const handleImageClick = (index: number) => {
        setViewerIndex(index);
        setViewerOpen(true);
    };

    return (
        <>
            <ImageSelectModal
                open={showModal}
                onClose={() => setShowModal(false)}
                mode="multiple"
                selectedIds={selectedImageIds}
                onDone={selectImages}
                productId={productId}
            />
            {/* Disable dragging context if disabled */}
            <DndContext sensors={sensors} onDragEnd={!disabled ? handleDragEnd : undefined}>
                <SortableContext items={selectedImages.map((img) => img.id)} strategy={rectSortingStrategy}>
                    <div className="image-grid">
                        {/* Render selected images */}
                        {selectedImages.length > 0 &&
                            selectedImages.map((image, index) => (
                                <SortableItem key={image.id} id={image.id} index={index} disabled={disabled}>
                                    <div
                                        className={`image-grid-item ${index === 0 ? 'image-grid-item-large' : ''}`}
                                        onClick={() => handleImageClick(index)}
                                    >
                                        <img src={image.thumbnailUrl || image.url} alt="Product" />
                                        <div className="image-overlay"></div>
                                        {/* "Cover" says what the position means; "featured"
                                            named a state without saying what it did. The shared
                                            Badge, not a bespoke `.badge` div, so the one chip on
                                            this screen is the same chip as everywhere else — and
                                            so it reads as a badge to anything inspecting the
                                            page rather than as a stray line of text. */}
                                        {index === 0 && (
                                            <Badge className="cover-badge absolute left-1.5 top-1.5 bg-foreground px-1.5 py-0.5 text-[10px] text-background">
                                                Cover
                                            </Badge>
                                        )}
                                        {/*
                                          One control, where you would reach for it. There used
                                          to be a dark bar across the bottom of every tile
                                          carrying a vertical-ellipsis "drag handle" that
                                          dragged nothing — the whole tile is the drag target —
                                          and read as an overflow menu that opened no menu.
                                        */}
                                        {!disabled && (
                                            <button
                                                type="button"
                                                name="delete-button"
                                                aria-label="Remove image"
                                                className="delete-button"
                                                onClick={(e) => handleDeleteImage(index, e)}
                                            >
                                                <TrashIcon className="icon" />
                                            </button>
                                        )}
                                    </div>
                                </SortableItem>
                            ))}

                        {/* Show "Add Image Placeholder" when not disabled */}
                        {!disabled && (
                            <AddImagePlaceholder
                                onAdd={() => {
                                    setShowModal(true);
                                }}
                                isFirstPlaceholder={selectedImages.length === 0}
                            />
                        )}

                        {/* Show placeholder when disabled and no images */}
                        {disabled && selectedImages.length === 0 && (
                            <div className={`image-grid-item image-grid-item-large flex items-center justify-center bg-white border border-gray-300`}>
                                <PhotoIcon
                                    className="flex-shrink-0 w-20 h-20 text-gray-300"
                                    aria-hidden="true"
                                />
                            </div>
                        )}
                    </div>
                </SortableContext>
            </DndContext>
            {viewerOpen && (
                <ProductImageViewer
                    images={selectedImages.map(img => img.url)}
                    initialIndex={viewerIndex}
                    onClose={() => setViewerOpen(false)}
                />
            )}
        </>
    );
};

export default ProductImageManager;
